import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import studentsService from '../services/students';
import SalesPersonSelect from './SalesPersonSelect';
import { getCountries, getStates, getCities } from '../data/locationData';
import { getAvailableTimezones, normalizeTimezoneForStorage } from '../utils/timezoneUtils';
import { getCountryFromTimezone } from '../data/timezones';
import { getPhoneCode } from '../data/phoneCodes';
import { calculateAgeFromCalendarYmd } from '../utils/dateTimeUtils';
import { AGE_GROUPS, ageToAgeGroup, getAgeGroupSelectOptions } from '../constants/ageGroups';

// Country codes for phone numbers
const countryCodes = [
  { value: '+1', label: '🇺🇸 +1 (US/CA)' },
  { value: '+44', label: '🇬🇧 +44 (UK)' },
  { value: '+61', label: '🇦🇺 +61 (AU)' },
  { value: '+91', label: '🇮🇳 +91 (IN)' },
  { value: '+49', label: '🇩🇪 +49 (DE)' },
  { value: '+33', label: '🇫🇷 +33 (FR)' },
  { value: '+81', label: '🇯🇵 +81 (JP)' },
  { value: '+55', label: '🇧🇷 +55 (BR)' },
  { value: '+52', label: '🇲🇽 +52 (MX)' },
  { value: '+39', label: '🇮🇹 +39 (IT)' },
  { value: '+34', label: '🇪🇸 +34 (ES)' },
  { value: '+31', label: '🇳🇱 +31 (NL)' },
  { value: '+46', label: '🇸🇪 +46 (SE)' },
  { value: '+47', label: '🇳🇴 +47 (NO)' },
  { value: '+45', label: '🇩🇰 +45 (DK)' },
  { value: '+358', label: '🇫🇮 +358 (FI)' },
  { value: '+41', label: '🇨🇭 +41 (CH)' },
  { value: '+43', label: '🇦🇹 +43 (AT)' },
  { value: '+32', label: '🇧🇪 +32 (BE)' },
  { value: '+86', label: '🇨🇳 +86 (CN)' },
  { value: '+82', label: '🇰🇷 +82 (KR)' },
  { value: '+65', label: '🇸🇬 +65 (SG)' },
  { value: '+971', label: '🇦🇪 +971 (AE)' },
  { value: '+64', label: '🇳🇿 +64 (NZ)' }
];

// Map country (locationData key) to default phone country code when country is chosen first
const countryToPhoneCode = {
  USA: '+1',
  Canada: '+1',
  UK: '+44',
  Australia: '+61',
  India: '+91',
  'New Zealand': '+64',
  Singapore: '+65',
  Japan: '+81',
  UAE: '+971',
  Germany: '+49',
  Indonesia: '+62',
  Kuwait: '+965',
  Netherlands: '+31',
  Oman: '+968',
  'Saudi Arabia': '+966',
  Sweden: '+46',
};

// Helper: treat empty string / null as undefined so .optional() accepts (Yup v1 doesn't use .allow())
const emptyToUndefined = (v) => (v == null || v === '' ? undefined : v);

const schema = yup.object().shape({
  user: yup.object().shape({
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string()
      .transform(emptyToUndefined)
      .optional()
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: yup.string()
      .transform(emptyToUndefined)
      .optional()
      .when('password', {
        is: (p) => p && String(p).length > 0,
        then: (s) => s.oneOf([yup.ref('password'), null], 'Passwords must match').required('Confirm password is required')
      }),
    first_name: yup.string().min(2, 'Primary contact first name must be at least 2 characters').required('Primary contact first name is required'),
    last_name: yup.string().min(2, 'Primary contact last name must be at least 2 characters').required('Primary contact last name is required'),
    country: yup.string().required('Country is required'),
    phone_country_code: yup.string().when('phone', {
      is: (phone) => phone && phone.length > 0,
      then: (schema) => schema.required('Country code is required when phone number is provided'),
      otherwise: (schema) => schema.notRequired()
    }),
    phone: yup.string()
      .matches(/^[\d\s\-()]+$/, 'Invalid phone number format (only digits, spaces, hyphens, and parentheses)')
      .required('Phone / contact is required'),
    timezone: yup.string().transform(emptyToUndefined).optional().default('UTC')
  }),
  student: yup.object().shape({
    first_name: yup.string().min(2, 'Student first name must be at least 2 characters').required('Student first name is required'),
    last_name: yup.string().min(2, 'Student last name must be at least 2 characters').required('Student last name is required'),
    date_of_birth: yup.date().max(new Date(), 'Date of birth cannot be in the future').optional().nullable(),
    age: yup.number().integer().min(0).max(120).transform((v, orig) => (orig === '' || orig == null ? undefined : v)).optional(),
    age_group: yup.string().oneOf(AGE_GROUPS).required('Age is required'),
    class_grade: yup.string().transform(emptyToUndefined).optional(),
    state: yup.string().transform(emptyToUndefined).optional(),
    city: yup.string().transform(emptyToUndefined).optional(),
    address: yup.string().trim().required('Street address is required'),
    postal_code: yup.string().trim().required('Zip code is required').max(20, 'Zip code must be at most 20 characters'),
    school_name: yup.string().transform(emptyToUndefined).optional(),
    school_address: yup.string().transform(emptyToUndefined).optional(),
    school_location: yup.string().transform(emptyToUndefined).optional(),
    hobby: yup.string().transform(emptyToUndefined).optional(),
    fees: yup.string().transform(emptyToUndefined).optional(),
    discount: yup.string().transform(emptyToUndefined).optional(),
    notes: yup.string().transform(emptyToUndefined).optional(),
    student_type: yup.string().transform(emptyToUndefined).oneOf(['normal', 'autistic', 'special_needs']).optional(),
    class_type_preference: yup.string().transform(emptyToUndefined).oneOf(['one_on_one', 'group']).optional(),
    learning_level: yup.string().transform(emptyToUndefined).oneOf(['beginner', 'intermediate', 'advanced']).optional(),
    support_requirements: yup.string().transform(emptyToUndefined).optional(),
    mother_name: yup.string().trim().required('Mother name is required'),
    father_name: yup.string().transform(emptyToUndefined).optional(),
    emergency_contact_name: yup.string().transform(emptyToUndefined).optional(),
    emergency_contact_phone_country_code: yup.string().transform(emptyToUndefined).optional(),
    emergency_contact_phone: yup.string()
      .transform(emptyToUndefined)
      .matches(/^[\d\s\-()]*$/, 'Invalid phone number format')
      .optional()
  }),
  time_preferences: yup.array().of(
    yup.object().shape({
      day_of_week: yup.number().min(0).max(6).required(),
      start_time: yup.string().matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, 'Invalid time format').required(),
      end_time: yup.string().matches(/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, 'Invalid time format').required(),
      is_preferred: yup.boolean()
    })
  ).optional().default([])
});

const StudentRegistrationForm = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      user: {
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone_country_code: '+1',
        phone: '',
        country: '',
        timezone: ''
      },
      student: {
        first_name: '',
        last_name: '',
        date_of_birth: '',
        age: '',
        age_group: '',
        class_grade: '',
        state: '',
        city: '',
        address: '',
        postal_code: '',
        school_name: '',
        school_address: '',
        school_location: '',
        hobby: '',
        fees: '',
        discount: '',
        notes: '',
        student_type: 'normal',
        class_type_preference: '',
        learning_level: 'beginner',
        support_requirements: '',
        mother_name: '',
        father_name: '',
        emergency_contact_name: '',
        emergency_contact_phone_country_code: '+1',
        emergency_contact_phone: '',
        assigned_to: ''
      },
      time_preferences: []
    }
  });

  const timePreferences = watch('time_preferences');
  const selectedCountry = watch('user.country');
  const selectedState = watch('student.state');

  const calculateAgeFromDob = (dob) => {
    const age = calculateAgeFromCalendarYmd(dob);
    return age == null ? '' : age;
  };

  // When country is selected, pre-select the matching phone country code
  useEffect(() => {
    if (selectedCountry && countryToPhoneCode[selectedCountry]) {
      setValue('user.phone_country_code', countryToPhoneCode[selectedCountry]);
    }
  }, [selectedCountry, setValue]);

  // No default time preferences - user must add them manually

  const addTimePreference = () => {
    const newPreference = {
      day_of_week: 0,
      start_time: '09:00:00',
      end_time: '10:00:00',
      is_preferred: true
    };
    const currentPreferences = timePreferences || [];
    const updatedPreferences = [...currentPreferences, newPreference];
    // Update the form value using setValue
    setValue('time_preferences', updatedPreferences);
  };

  const removeTimePreference = (index) => {
    const updatedPreferences = timePreferences.filter((_, i) => i !== index);
    // Update the form value using setValue
    setValue('time_preferences', updatedPreferences);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // Remove confirmPassword from the data
      const { confirmPassword, phone_country_code, ...userData } = data.user;
      
      // Combine country code with phone number if phone is provided
      if (data.user.phone && data.user.phone_country_code) {
        userData.phone = `${data.user.phone_country_code}${data.user.phone}`;
      }
      if (userData.timezone) {
        userData.timezone = normalizeTimezoneForStorage(userData.timezone);
      }

      // Combine country code with emergency contact phone
      const { emergency_contact_phone_country_code, ...studentData } = data.student;
      if (data.student.emergency_contact_phone && data.student.emergency_contact_phone_country_code) {
        studentData.emergency_contact_phone = `${data.student.emergency_contact_phone_country_code}${data.student.emergency_contact_phone}`;
      }

      const numericStudentFields = { ...studentData };
      if (numericStudentFields.age === '' || numericStudentFields.age == null) {
        delete numericStudentFields.age;
      } else {
        numericStudentFields.age = parseInt(numericStudentFields.age, 10);
      }
      if (numericStudentFields.fees === '' || numericStudentFields.fees == null) {
        delete numericStudentFields.fees;
      } else {
        numericStudentFields.fees = parseFloat(numericStudentFields.fees);
      }
      if (numericStudentFields.discount === '' || numericStudentFields.discount == null) {
        delete numericStudentFields.discount;
      } else {
        numericStudentFields.discount = parseFloat(numericStudentFields.discount);
      }
      if (!numericStudentFields.assigned_to) {
        delete numericStudentFields.assigned_to;
      }

      const submitData = {
        user: userData,
        student: { ...numericStudentFields, status: 'registered' },
        time_preferences: data.time_preferences
      };

      const result = await studentsService.createStudent(submitData);
      
      if (result.success) {
        reset();
        onSuccess(result.data);
        onClose();
      } else {
        setError(result.message || 'Failed to create student');
      }
    } catch (error) {
      const serverMsg = error.response?.data?.message;
      const serverErrors = error.response?.data?.errors;
      const details = Array.isArray(serverErrors) ? `: ${serverErrors.join(', ')}` : '';
      setError(serverMsg ? `${serverMsg}${details}` : 'An error occurred while creating the student');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Register New Student</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="h6" gutterBottom>
            Primary Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Primary Contact First Name"
                    error={!!errors.user?.first_name}
                    helperText={errors.user?.first_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Primary Contact Last Name"
                    error={!!errors.user?.last_name}
                    helperText={errors.user?.last_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.user?.email}
                    helperText={errors.user?.email?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.country"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.user?.country}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      {...field}
                      label="Country"
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('student.state', '');
                        setValue('student.city', '');
                      }}
                    >
                      {getCountries().map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.user?.country && (
                      <Typography variant="caption" color="error">
                        {errors.user.country.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Phone / contact
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Controller
                  name="user.phone_country_code"
                  control={control}
                  render={({ field }) => (
                    <FormControl sx={{ width: 180 }} error={!!errors.user?.phone_country_code}>
                      <InputLabel>Code</InputLabel>
                      <Select {...field} label="Code">
                        {countryCodes.map(code => (
                          <MenuItem key={code.value} value={code.value}>
                            {code.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="user.phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      sx={{ flex: 1 }}
                      fullWidth
                      placeholder="123-456-7890"
                      error={!!errors.user?.phone}
                      helperText={errors.user?.phone?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Password (optional)"
                    type="password"
                    autoComplete="new-password"
                    error={!!errors.user?.password}
                    helperText={errors.user?.password?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Confirm Password (optional)"
                    type="password"
                    autoComplete="new-password"
                    error={!!errors.user?.confirmPassword}
                    helperText={errors.user?.confirmPassword?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="user.timezone"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.user?.timezone}>
                    <InputLabel>Timezone (optional)</InputLabel>
                    <Select
                      {...field}
                      label="Timezone"
                      onChange={(e) => {
                        field.onChange(e);
                        const countryFromTz = getCountryFromTimezone(e.target.value);
                        if (countryFromTz) {
                          setValue('user.phone_country_code', getPhoneCode(countryFromTz));
                        }
                      }}
                    >
                      {getAvailableTimezones().map((timezone) => (
                        <MenuItem key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.user?.timezone && (
                      <Typography variant="caption" color="error">
                        {errors.user.timezone.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Student Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Student First Name"
                    error={!!errors.student?.first_name}
                    helperText={errors.student?.first_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Student Last Name"
                    error={!!errors.student?.last_name}
                    helperText={errors.student?.last_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.date_of_birth"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date of Birth (optional)"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.student?.date_of_birth}
                    helperText={errors.student?.date_of_birth?.message}
                    onChange={(e) => {
                      field.onChange(e);
                      const age = calculateAgeFromDob(e.target.value);
                      if (age !== '') {
                        setValue('student.age', age);
                        const group = ageToAgeGroup(age);
                        if (group) setValue('student.age_group', group);
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.age"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Age (optional)"
                    type="number"
                    error={!!errors.student?.age}
                    helperText={errors.student?.age?.message || 'Auto-calculated from date of birth'}
                    onChange={(e) => {
                      field.onChange(e);
                      const group = ageToAgeGroup(e.target.value);
                      if (group) setValue('student.age_group', group);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.age_group"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.student?.age_group}>
                    <InputLabel>Age Group</InputLabel>
                    <Select {...field} label="Age Group">
                      {getAgeGroupSelectOptions(field.value).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                    {errors.student?.age_group && (
                      <Typography variant="caption" color="error">
                        {errors.student.age_group.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.class_grade"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Class Grade (optional)"
                    error={!!errors.student?.class_grade}
                    helperText={errors.student?.class_grade?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.mother_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Mother name (parents name)"
                    error={!!errors.student?.mother_name}
                    helperText={errors.student?.mother_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.father_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Father name (parents name)"
                    error={!!errors.student?.father_name}
                    helperText={errors.student?.father_name?.message}
                  />
                )}
              />
            </Grid>
            {errors.student && typeof errors.student === 'object' && 'message' in errors.student && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  {errors.student.message}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.state"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.student?.state} disabled={!selectedCountry}>
                    <InputLabel>State (optional)</InputLabel>
                    <Select
                      {...field}
                      label="State (optional)"
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('student.city', '');
                      }}
                    >
                      {selectedCountry && getStates(selectedCountry).map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.city"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.student?.city} disabled={!selectedState}>
                    <InputLabel>City (optional)</InputLabel>
                    <Select {...field} label="City (optional)">
                      {selectedCountry && selectedState && getCities(selectedCountry, selectedState).map((city) => (
                        <MenuItem key={city} value={city}>
                          {city}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="student.address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Street address *"
                    multiline
                    rows={2}
                    error={!!errors.student?.address}
                    helperText={errors.student?.address?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.postal_code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Zip code *"
                    error={!!errors.student?.postal_code}
                    helperText={errors.student?.postal_code?.message}
                    inputProps={{ maxLength: 20 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.student_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.student?.student_type}>
                    <InputLabel>Student Type (optional)</InputLabel>
                    <Select {...field} label="Student Type">
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="autistic">Autistic</MenuItem>
                      <MenuItem value="special_needs">Special Needs</MenuItem>
                    </Select>
                    {errors.student?.student_type && (
                      <Typography variant="caption" color="error">
                        {errors.student.student_type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.class_type_preference"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.student?.class_type_preference}>
                    <InputLabel>Class Type Preference (optional)</InputLabel>
                    <Select {...field} label="Class Type Preference">
                      <MenuItem value="one_on_one">One-on-One</MenuItem>
                      <MenuItem value="group">Group</MenuItem>
                    </Select>
                    {errors.student?.class_type_preference && (
                      <Typography variant="caption" color="error">
                        {errors.student.class_type_preference.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.learning_level"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.student?.learning_level}>
                    <InputLabel>Learning Level (optional)</InputLabel>
                    <Select {...field} label="Learning Level">
                      <MenuItem value="beginner">Beginner</MenuItem>
                      <MenuItem value="intermediate">Intermediate</MenuItem>
                      <MenuItem value="advanced">Advanced</MenuItem>
                    </Select>
                    {errors.student?.learning_level && (
                      <Typography variant="caption" color="error">
                        {errors.student.learning_level.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.emergency_contact_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Emergency Contact Name (optional)"
                    error={!!errors.student?.emergency_contact_name}
                    helperText={errors.student?.emergency_contact_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Emergency Contact Phone (optional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Controller
                  name="student.emergency_contact_phone_country_code"
                  control={control}
                  render={({ field }) => (
                    <FormControl sx={{ width: 180 }} error={!!errors.student?.emergency_contact_phone_country_code}>
                      <InputLabel>Code</InputLabel>
                      <Select {...field} label="Code">
                        {countryCodes.map(code => (
                          <MenuItem key={code.value} value={code.value}>
                            {code.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="student.emergency_contact_phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      sx={{ flex: 1 }}
                      fullWidth
                      placeholder="123-456-7890"
                      error={!!errors.student?.emergency_contact_phone}
                      helperText={errors.student?.emergency_contact_phone?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="student.support_requirements"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Support Requirements (optional)"
                    multiline
                    rows={3}
                    error={!!errors.student?.support_requirements}
                    helperText={errors.student?.support_requirements?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            School Information (optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.school_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="School Name"
                    error={!!errors.student?.school_name}
                    helperText={errors.student?.school_name?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.school_location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="School Location"
                    error={!!errors.student?.school_location}
                    helperText={errors.student?.school_location?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="student.school_address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="School Address"
                    multiline
                    rows={2}
                    error={!!errors.student?.school_address}
                    helperText={errors.student?.school_address?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Additional Information (optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.hobby"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Hobby"
                    error={!!errors.student?.hobby}
                    helperText={errors.student?.hobby?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.fees"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Fees"
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    error={!!errors.student?.fees}
                    helperText={errors.student?.fees?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.discount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Discount (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                    error={!!errors.student?.discount}
                    helperText={errors.student?.discount?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="student.assigned_to"
                control={control}
                render={({ field }) => (
                  <SalesPersonSelect
                    value={field.value || ''}
                    onChange={(v) => field.onChange(v || '')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="student.notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Notes"
                    multiline
                    rows={4}
                    error={!!errors.student?.notes}
                    helperText={errors.student?.notes?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Time Preferences (optional)
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addTimePreference}
            >
              Add Time Preference
            </Button>
          </Box>

          {timePreferences?.map((preference, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Day</InputLabel>
                    <Select
                      value={preference.day_of_week || 0}
                                             onChange={(e) => {
                         const updatedPreferences = [...timePreferences];
                         updatedPreferences[index].day_of_week = e.target.value;
                         // Update form using setValue
                         setValue('time_preferences', updatedPreferences);
                       }}
                      label="Day"
                    >
                      {daysOfWeek.map(day => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={preference.start_time || '09:00'}
                                         onChange={(e) => {
                       const updatedPreferences = [...timePreferences];
                       updatedPreferences[index].start_time = e.target.value + ':00';
                       // Update form using setValue
                       setValue('time_preferences', updatedPreferences);
                     }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={preference.end_time || '10:00'}
                                         onChange={(e) => {
                       const updatedPreferences = [...timePreferences];
                       updatedPreferences[index].end_time = e.target.value + ':00';
                       // Update form using setValue
                       setValue('time_preferences', updatedPreferences);
                     }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Chip
                    label={preference.is_preferred ? 'Preferred' : 'Available'}
                    color={preference.is_preferred ? 'primary' : 'default'}
                                         onClick={() => {
                       const updatedPreferences = [...timePreferences];
                       updatedPreferences[index].is_preferred = !preference.is_preferred;
                       // Update form using setValue
                       setValue('time_preferences', updatedPreferences);
                     }}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton
                    color="error"
                    onClick={() => removeTimePreference(index)}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}

          {errors.time_preferences && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.time_preferences.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Creating...' : 'Create Student'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default StudentRegistrationForm; 